import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { PopoverBody, Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EditIcon } from 'assets/icon-edit.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { ReactComponent as UserIcon } from 'assets/icon-user.svg';
import { ReactComponent as LockIcon } from 'assets/icon-lock.svg';
import { ReactComponent as UnlockIcon } from 'assets/icon-unlock.svg';
import { ReactComponent as ConversationIcon } from 'assets/icon-conversation.svg';
import { ReactComponent as FilterIcon } from 'assets/icon-filter.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-refresh.svg';
import { ReactComponent as FilterAppliedIcon } from 'assets/icon-filter-applied.svg';

import { ButtonIcon } from 'shared/components/Buttons';
import { LabelIcon } from 'shared/components/Labels';
import Pagination from 'shared/components/Pagination';
import MainSpinner from 'shared/components/MainSpinner';
import FilterDropdownInput from 'shared/components/FilterDropdownInput';
import FilterMultiSelect from 'shared/components/FilterMultiSelect';
import {
  TableHeader,
  TableHeaderText,
  Popover,
  OptionMenu,
} from 'shared/components/SharedStyles/SharedStyles';

import {
  editingContact,
  paginateContacts,
  refreshContacts,
  selectRemoveContact,
  setClearContactList,
  loadContacts,
  setOpenDelete,
} from 'redux/actions/contactActions';
import { showAlert } from 'redux/actions/alertActions';

import useApi from 'hooks/useApi';

import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from 'shared/utils';
import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { colors } from 'shared/defaultTheme';
import { setNewCommunication } from 'redux/actions/clientActions';

const PAGE_SIZE = 10;

const ContactsTable = ({ clientId, fetchContacts, loadingContacts }) => {
  const dispatch = useDispatch();
  const { cusNavId } = useSelector((state) => state.auth);
  const { contacts, currentContacts, sortConfig, searchValue, clearList } = useSelector(
    (state) => state.contact
  );
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const theme = useSelector((state) => state.theme);
  const { syncContacts } = theme;
  const [columnSearch, setColumnSearch] = useState({
    name: undefined,
    surname: undefined,
    description: undefined,
    phone: undefined,
    mail: undefined,
    hasAccess: undefined,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [{ loading }, postAccess] = customAreaApi(
    {
      url: `${window.API_V1}/login/access?admin=false`,
      method: 'POST',
    },
    { manual: true }
  );
  const [{ loadingAccessResend }, postAccessResend] = customAreaApi(
    {
      url: `${window.API_V1}/login/access/resend?admin=false`,
      method: 'POST',
    },
    { manual: true }
  );
  const [{ loadingRevokeAccess }, postRevokeAccess] = customAreaApi(
    {
      url: `${window.API_V1}/login/access/revoke?admin=false`,
      method: 'POST',
    },
    { manual: true }
  );

  const onFirstPage = () => {
    dispatch(paginateContacts({ page: 1, pageSize: PAGE_SIZE }));
    setCurrentPage(1);
  };

  const onLastPage = () => {
    dispatch(paginateContacts({ page: lastPage, pageSize: PAGE_SIZE }));
    setCurrentPage(lastPage);
  };

  const onNextPage = () => {
    const page = currentPage + 1;
    if (page > lastPage) {
      return;
    }
    dispatch(paginateContacts({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onPreviousPage = () => {
    const page = currentPage - 1;
    if (page <= 0) {
      return;
    }
    dispatch(paginateContacts({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onGoToPage = (page) => {
    dispatch(paginateContacts({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  useEffect(() => {
    const currentLastPage = Math.ceil(contacts.length / PAGE_SIZE);
    onFirstPage();
    setLastPage(currentLastPage);
  }, [contacts]);

  const getOptionsFromAllContact = (fieldValue, fieldName, fieldValueTwo, fieldNameTwo) => {
    if (fieldValue === fieldName && fieldValueTwo !== undefined && fieldNameTwo === fieldValueTwo) {
      const values = Array.from(
        new Set(
          contacts.map(
            (currentContact) => `${currentContact[fieldValue]} ${currentContact[fieldValueTwo]}`
          )
        )
      );
      return values.map((value) => ({ value, name: value }));
    }

    if (fieldValue === fieldName) {
      const values = Array.from(
        new Set(contacts.map((currentContact) => currentContact[fieldValue]))
      );
      return values.map((value) => ({ value, name: value }));
    }
    return contacts.map((currentContact) => ({
      value: currentContact[fieldValue],
      name: currentContact[fieldName],
    }));
  };

  const searchContacts = async (newColumnSearch) => {
    const searchParam =
      sortConfig.length > 0
        ? {
            filters: [
              {
                model: 'ClientAccess',
                field: 'ClientId',
                filter: [clientId],
              },
            ],
            orders: [...sortConfig],
            reduce: {
              limit: 0,
              skip: 0,
            },
          }
        : {
            filters: [
              {
                model: 'ClientAccess',
                field: 'ClientId',
                filter: [clientId],
              },
            ],
            orders: [{ model: 'Contact', field: 'Name', asc: true }],
            reduce: {
              limit: 0,
              skip: 0,
            },
          };
    if (newColumnSearch) {
      Object.keys(newColumnSearch).forEach((key) => {
        if (newColumnSearch[key]) {
          searchParam.filters.push(newColumnSearch[key]);
        }
      });
    }

    if (searchValue && searchValue.length >= 2) {
      searchParam.filterText = {
        regexFilter: searchValue,
      };
    }
    fetchContacts(searchParam);
  };

  useEffect(() => {
    if (sortConfig.length > 0) {
      searchContacts();
    }
  }, [sortConfig]);

  useEffect(() => {
    if (searchValue != null && !loadingContacts) {
      searchContacts(columnSearch);
    }
  }, [searchValue]);

  useEffect(() => {
    if (sortConfig.length > 0) {
      searchContacts(columnSearch);
    }
  }, [sortConfig]);

  useEffect(() => {
    if (clearList) {
      setColumnSearch({
        name: undefined,
        surname: undefined,
        description: undefined,
        phone: undefined,
        mail: undefined,
        hasAccess: undefined,
      });
      dispatch(setClearContactList(false));
      fetchContacts();
    }
  }, [clearList]);

  const onSearchOptionOrText = async ({
    selectedOption,
    searchText,
    fieldSelectedOption,
    fieldSearchText,
    fieldSearchTextTwo,
    column,
    columnTwo,
  }) => {
    const newColumnSearch = { ...columnSearch };
    newColumnSearch[column] = undefined;
    if (columnTwo !== undefined) {
      newColumnSearch[columnTwo] = undefined;
    }
    if (selectedOption) {
      if (column && columnTwo !== '') {
        newColumnSearch[column] = {
          model: 'Contact',
          field: fieldSelectedOption,
          filter: [selectedOption.split(' ')[0]],
        };
      }
      if (column && columnTwo === '') {
        newColumnSearch[column] = {
          model: 'Contact',
          field: fieldSelectedOption,
          filter: [selectedOption],
        };
      }
    } else if (searchText) {
      if (column && columnTwo !== '') {
        newColumnSearch[column] = {
          model: 'Contact',
          orSubFilters: [
            {
              andFilter: [
                {
                  model: 'Contact',
                  field: fieldSearchText,
                  filter: [searchText],
                  isRegex: true,
                },
              ],
            },
            {
              andFilter: [
                {
                  model: 'Contact',
                  field: fieldSearchTextTwo,
                  filter: [searchText],
                  isRegex: true,
                },
              ],
            },
          ],
        };
      }
      if (column && columnTwo === '') {
        newColumnSearch[column] = {
          model: 'Contact',
          field: fieldSearchText,
          filter: [searchText],
          isRegex: true,
        };
      }
    }
    searchContacts(newColumnSearch);
    setColumnSearch(newColumnSearch);
  };

  const onSearchMultipleOptions = async ({ selectedOptions, fieldSearch, column }) => {
    const newColumnSearch = { ...columnSearch };
    newColumnSearch[column] = undefined;
    if (selectedOptions && selectedOptions.length > 0 && fieldSearch === 'Mail') {
      newColumnSearch[column] = {
        model: 'Contact',
        field: fieldSearch,
        filter: selectedOptions,
      };
    }
    if (selectedOptions && selectedOptions.length > 0 && fieldSearch === 'ContactState') {
      newColumnSearch[column] = {
        model: 'ClientAccess',
        field: fieldSearch,
        filter: selectedOptions,
      };
    }
    searchContacts(newColumnSearch);
    setColumnSearch(newColumnSearch);
  };

  const togglePopover = (contact) => {
    dispatch(
      loadContacts(
        contacts.map((item) =>
          item.mail === contact.mail ? { ...item, popoverOpen: !item.popoverOpen } : item
        )
      )
    );
  };

  const onEdit = (contact) => dispatch(editingContact(contact));

  const onDelete = (contact) => {
    dispatch(selectRemoveContact(contact));
    dispatch(setOpenDelete(true));
  };

  const onGiveAccess = async (contact) => {
    try {
      const {
        data: { errors },
      } = await postAccess({
        data: {
          login: contact.mail,
          idClient: contact.clientId,
          idCustomer: cusNavId,
          domain: window.location.origin,
        },
      });
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      dispatch(refreshContacts(true));
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
    } catch (ex) {
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
      console.error('error giving access: ', ex);
    }
  };
  const onRevokeAccess = async (contact) => {
    try {
      const {
        data: { errors },
      } = await postRevokeAccess({
        data: {
          login: contact.mail,
          idClient: contact.clientId,
          idCustomer: cusNavId,
          domain: window.location.origin,
        },
      });
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      dispatch(refreshContacts(true));
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
    } catch (ex) {
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
      console.error('error revoking access: ', ex);
    }
  };
  const onResendInstruction = async (contact) => {
    try {
      const {
        data: { errors },
      } = await postAccessResend({
        data: {
          login: contact.mail,
          idClient: contact.clientId,
          idCustomer: cusNavId,
          domain: window.location.origin,
        },
      });
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      dispatch(refreshContacts(true));
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
    } catch (ex) {
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
      console.error('error resending access: ', ex);
    }
  };

  const handleTypeEventClick = (e, type, contact) => {
    const { localName } = e.target;
    if (type === 'popover') {
      togglePopover(contact);
    }
    if (type === 'edit' && (localName === 'div' || localName === 'td')) {
      onEdit(contact);
    }
    if (type === 'btnEdit') {
      onEdit(contact);
    }
    if (type === 'delete') {
      onDelete(contact);
    }
    if (type === 'resendInstruction') {
      onResendInstruction(contact);
    }
    if (type === 'revokeAccess') {
      onRevokeAccess(contact);
    }
    if (type === 'giveAccess') {
      onGiveAccess(contact);
    }
  };

  const showAccessIcon = (contact, index) => {
    if (contact.contactState === 'sended') {
      return (
        <>
          <LockIcon
            id={`menuActions_${index}`}
            fill={colors.indochine}
            height={20}
            width={15}
            onClick={(e) => handleTypeEventClick(e, 'popover', contact)}
          />
        </>
      );
    }
    if (contact.contactState === 'access') {
      return (
        <>
          <UnlockIcon
            id={`menuActions_${index}`}
            fill={colors.bilbao}
            height={20}
            width={15}
            onClick={(e) => handleTypeEventClick(e, 'popover', contact)}
          />
        </>
      );
    }
    return (
      <>
        <LockIcon
          id={`menuActions_${index}`}
          fill={colors.error}
          height={20}
          width={15}
          onClick={(e) => handleTypeEventClick(e, 'popover', contact)}
        />
      </>
    );
  };

  return (
    <div className="control-panel__table" id="contact-data-table">
      <Table className={!currentContacts.length && 'table-notfound'}>
        <thead>
          <tr>
            <th className="default-column">
              <TableHeader>
                <TableHeaderText>{t('general_name')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterNameContactColumn"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.name ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  isDisabled={!clientId}
                  onClick={() => null}
                />
                {clientId && (
                  <FilterDropdownInput
                    dropdownOptions={getOptionsFromAllContact('name', 'name')}
                    target="filterNameContactColumn"
                    fieldSelectedOption="Name"
                    fieldSearchText="Name"
                    column="name"
                    onSearch={onSearchOptionOrText}
                  />
                )}
              </TableHeader>
            </th>
            <th className="default-column">
              <TableHeader>
                <TableHeaderText>{t('general_last_name')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterSurnameColumn"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.surname ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  isDisabled={!clientId}
                  onClick={() => null}
                />
                {clientId && (
                  <FilterDropdownInput
                    dropdownOptions={getOptionsFromAllContact(
                      'surname',
                      'surname',
                      'surnameTwo',
                      'surnameTwo'
                    )}
                    target="filterSurnameColumn"
                    fieldSelectedOption="Surname"
                    fieldSearchText="Surname"
                    fieldSearchTextTwo="SurnameTwo"
                    column="surname"
                    columnTwo="surnameTwo"
                    onSearch={onSearchOptionOrText}
                  />
                )}
              </TableHeader>
            </th>
            <th className="large-column">
              <TableHeader>
                <TableHeaderText>{t('general_email')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterMailColumn"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.mail ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  onClick={() => null}
                  isDisabled={!clientId}
                />
                {clientId && (
                  <FilterMultiSelect
                    options={getOptionsFromAllContact('mail', 'mail')}
                    target="filterMailColumn"
                    fieldSearch="Mail"
                    column="mail"
                    onSearch={onSearchMultipleOptions}
                  />
                )}
              </TableHeader>
            </th>
            <th className="default-column">
              <TableHeader>
                <TableHeaderText>{t('general_phone')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterPhoneColumn"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.phone ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  isDisabled={!clientId}
                  onClick={() => null}
                />
                {clientId && (
                  <FilterDropdownInput
                    dropdownOptions={getOptionsFromAllContact('phone', 'phone')}
                    target="filterPhoneColumn"
                    fieldSelectedOption="Phone"
                    fieldSearchText="Phone"
                    column="phone"
                    onSearch={onSearchOptionOrText}
                  />
                )}
              </TableHeader>
            </th>
            <th className="large-column">
              <TableHeader>
                <TableHeaderText>{t('general_description')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterDescriptionColumn"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.description ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  isDisabled={!clientId}
                  onClick={() => null}
                />
                {clientId && (
                  <FilterDropdownInput
                    dropdownOptions={getOptionsFromAllContact('description', 'description')}
                    target="filterDescriptionColumn"
                    fieldSelectedOption="Description"
                    fieldSearchText="Description"
                    column="description"
                    onSearch={onSearchOptionOrText}
                  />
                )}
              </TableHeader>
            </th>
            <th className="small-column">
              <TableHeader>
                <TableHeaderText>{t('client_management_access')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterHasAccessColumn"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.contactState ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  onClick={() => null}
                  isDisabled={!clientId}
                />
                {clientId && (
                  <FilterMultiSelect
                    options={getOptionsFromAllContact('contactState', 'contactState')}
                    target="filterHasAccessColumn"
                    fieldSearch="ContactState"
                    column="contactState"
                    onSearch={onSearchMultipleOptions}
                    type="boolean"
                  />
                )}
              </TableHeader>
            </th>
            <th className="small-column">
              <TableHeader>
                <TableHeaderText>{t('general_column_action')}</TableHeaderText>
              </TableHeader>
            </th>
          </tr>
        </thead>
        <tbody className="contacts-tbody">
          {currentContacts.length > 0 && (
            <>
              {currentContacts.map((contact, index) => (
                <tr
                  key={contact.mail}
                  onClick={(e) => handleTypeEventClick(e, 'edit', contact)}
                  className="action-row-contact"
                >
                  <td>
                    <div id={`name-${index}`} className="info-body-name">
                      {contact.name ?? ''}
                    </div>
                    <UncontrolledTooltip placement="bottom" target={`name-${index}`}>
                      {contact.name ?? ''}
                    </UncontrolledTooltip>
                  </td>
                  <td>
                    <div id={`surname-${index}`} className="info-body-surname">{`${
                      contact.surname ?? ''
                    } ${contact.surnameTwo ?? ''}`}</div>
                    <UncontrolledTooltip placement="bottom" target={`surname-${index}`}>
                      {`${contact.surname ?? ''} ${contact.surnameTwo ?? ''}`}
                    </UncontrolledTooltip>
                  </td>
                  <td>
                    <div id={`mail-${index}`} className="info-body-email">
                      {contact.mail ?? ''}
                    </div>
                    <UncontrolledTooltip placement="bottom" target={`mail-${index}`}>
                      {contact.mail ?? ''}
                    </UncontrolledTooltip>
                  </td>
                  <td>
                    <div id={`phone-${index}`} className="info-body-phone">
                      {contact.phone ?? ''}
                    </div>
                    <UncontrolledTooltip placement="bottom" target={`phone-${index}`}>
                      {contact.phone ?? ''}
                    </UncontrolledTooltip>
                  </td>
                  <td>
                    <div id={`description-${index}`} className="info-body-description">
                      {contact.description ?? ''}
                    </div>
                    <UncontrolledTooltip
                      placement="bottom"
                      target={`description-${index}`}
                      autohide={false}
                    >
                      {contact.description ?? ''}
                    </UncontrolledTooltip>
                  </td>
                  <td className="d-flex flex-row justify-content-center">
                    {showAccessIcon(contact, index)}
                    <Popover
                      trigger="legacy"
                      placement="left"
                      target={`menuActions_${index}`}
                      width={208}
                      isOpen={contact.popoverOpen}
                      toggle={() => togglePopover(contact)}
                    >
                      <PopoverBody>
                        {contact.contactState === 'access' && (
                          <>
                            <OptionMenu
                              height={40}
                              onClick={(e) => {
                                handleTypeEventClick(e, 'resendInstruction', contact);
                                togglePopover(contact);
                              }}
                              aria-hidden="true"
                            >
                              <LabelIcon
                                label={t('client_management_resend_instruction_access')}
                                Icon={() => (
                                  <LockIcon fill={colors.indochine} className="label-icon__send" />
                                )}
                                color={colors.pewter}
                                className="action-option-label body-text--primary"
                                classNameMain=""
                                isStyle={false}
                              />
                            </OptionMenu>
                            <OptionMenu
                              onClick={(e) => {
                                handleTypeEventClick(e, 'revokeAccess', contact);
                                togglePopover(contact);
                              }}
                              aria-hidden="true"
                            >
                              <LabelIcon
                                label={t('client_management_revoke_access')}
                                Icon={() => (
                                  <LockIcon fill={colors.error} className="label-icon__lock" />
                                )}
                                color={colors.pewter}
                                className="action-option-label body-text--primary"
                                classNameMain=""
                                isStyle={false}
                              />
                            </OptionMenu>
                          </>
                        )}
                        {contact.contactState === 'sended' && clientId && (
                          <>
                            <OptionMenu
                              onClick={(e) => {
                                handleTypeEventClick(e, 'giveAccess', contact);
                                togglePopover(contact);
                              }}
                              aria-hidden="true"
                            >
                              <LabelIcon
                                label={t('client_management_give_access')}
                                Icon={() => (
                                  <UnlockIcon fill={colors.bilbao} className="label-icon__lock" />
                                )}
                                color={colors.pewter}
                                className="action-option-label body-text--primary"
                                classNameMain=""
                                isStyle={false}
                              />
                            </OptionMenu>
                            <OptionMenu
                              onClick={(e) => {
                                handleTypeEventClick(e, 'revokeAccess', contact);
                                togglePopover(contact);
                              }}
                              aria-hidden="true"
                            >
                              <LabelIcon
                                label={t('client_management_revoke_access')}
                                Icon={() => (
                                  <LockIcon fill={colors.error} className="label-icon__lock" />
                                )}
                                color={colors.pewter}
                                className="action-option-label body-text--primary"
                                classNameMain=""
                                isStyle={false}
                              />
                            </OptionMenu>
                          </>
                        )}
                        {contact.contactState === 'created' && clientId && (
                          <>
                            <OptionMenu
                              onClick={(e) => {
                                handleTypeEventClick(e, 'giveAccess', contact);
                                togglePopover(contact);
                              }}
                              aria-hidden="true"
                            >
                              <LabelIcon
                                label={t('client_management_give_access')}
                                Icon={() => (
                                  <UnlockIcon fill={colors.bilbao} className="label-icon__lock" />
                                )}
                                color={colors.pewter}
                                className="action-option-label body-text--primary"
                                classNameMain=""
                                isStyle={false}
                              />
                            </OptionMenu>
                          </>
                        )}
                      </PopoverBody>
                    </Popover>
                  </td>
                  <td className="">
                    <div className="">
                      <ButtonIcon
                        id={`edit-icon-contact-${index}`}
                        title={t('clients_edit_client')}
                        type="button"
                        Icon={() => <EditIcon fill={colors.suvaGrey} />}
                        className="button-icon ml-n4"
                        onClick={(e) => handleTypeEventClick(e, 'btnEdit', contact)}
                      />
                      <ButtonIcon
                        id={`trash-icon-contact-${index}`}
                        title={t('general_delete')}
                        type="button"
                        Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                        className="button-icon"
                        onClick={(e) => handleTypeEventClick(e, 'delete', contact)}
                      />
                      {clientId && (
                        <ButtonIcon
                          id={`conversation-icon-client-${index}`}
                          title={t('notification_communication_new')}
                          type="button"
                          Icon={() => <ConversationIcon fill={colors.suvaGrey} />}
                          className="button-icon"
                          onClick={() => {
                            dispatch(setNewCommunication(contact.mail));
                          }}
                        />
                      )}
                      {syncContacts === 2 && (
                        <ButtonIcon
                          id={`sync-icon-contact-${index}`}
                          title={t('general_customizer_label_sync_client')}
                          type="button"
                          Icon={() => <RefreshIcon fill={colors.suvaGrey} />}
                          className="button-icon"
                          onClick={() => null}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </Table>
      {!contacts.length && (
        <div className="empty-data-container">
          <UserIcon />
          <span>{t('client_contact_not_found')}</span>
        </div>
      )}
      {contacts.length > 0 && (
        <Pagination
          currentPage={currentPage}
          lastPage={lastPage}
          totalData={contacts.length}
          onFirstPage={onFirstPage}
          onLastPage={onLastPage}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          onGoToPage={onGoToPage}
          titlePagination={t('client_management_contact')}
        />
      )}
      {(loading || loadingAccessResend || loadingRevokeAccess) && <MainSpinner />}
    </div>
  );
};

ContactsTable.propTypes = {
  clientId: PropTypes.string.isRequired,
  fetchContacts: PropTypes.func.isRequired,
  loadingContacts: PropTypes.bool.isRequired,
};

export default ContactsTable;
